import React, { useState } from 'react';
import slugify from 'slugify';
import { HiDocumentCheck } from 'react-icons/hi2';
import { DocumentInfo } from '../data/dataType';
import ToastMessage from './ToastMessage';
import { LoaderWrapper } from '@/components/Loader/LoaderWrapper';
import { createClient } from '@/app/utils/supabase/client';

export const DocumentUpload: React.FC<{
    itemId: number | string;
    bucket: string;
    folder: string;
    locale: string;
    value: DocumentInfo | undefined;
    onChange: (value: DocumentInfo) => void;
}> = ({ itemId, bucket, folder, locale, value, onChange }) => {
    const allowedFileTypes =
        'application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, text/plain, application/pdf';
    const [documentInfo, setDocumentInfo] = useState<DocumentInfo | undefined>(
        value,
    );
    const [viewDocument, setViewDocument] = useState(
        value &&
            value.bucket &&
            value.folder &&
            value.itemId &&
            value.name &&
            `${value.bucket}/${value.folder}/${value.itemId}/docs/${value.name}`,
    );
    const [isUploading, setIsUploading] = React.useState(false);

    const supabase = createClient();

    const dictionary: { [key: string]: { [key: string]: string } } = {
        fr: {
            clickUpload: 'Cliquer pour télécharger',
            dragDrop: 'ou glisser déposer',
            allowedFiles: 'SVG, PNG, JPG ou GIF',
            clickChange: 'Cliquer ici pour changer le document',
        },
        en: {
            clickUpload: 'Click to upload',
            dragDrop: 'or drag and drop',
            allowedFiles: 'SVG, PNG, JPG or GIF',
            clickChange: 'Click here to change document',
        },
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }
        const document = e.target.files[0];

        const documentInfo: DocumentInfo = {
            file: document,
            bucket: bucket,
            folder: folder,
            itemId: itemId,
            name: slugify(document.name),
            dirty: true,
        };
        setIsUploading(true);
        await uploadDocument(documentInfo);
        setIsUploading(false);
        setDocumentInfo(documentInfo);
        setViewDocument(
            `${bucket}/${folder}/${itemId}/docs/${documentInfo.name}`,
        );
        onChange(documentInfo);
    };

    const notify = React.useCallback((type: string, message: string) => {
        ToastMessage({ type, message });
    }, []);

    const uploadDocument = async (documentInfo: DocumentInfo) => {
        if (documentInfo.file) {
            const { error } = await supabase.storage
                .from(bucket)
                .upload(
                    `${folder}/${itemId}/docs/${documentInfo.name}`,
                    documentInfo.file,
                    {
                        cacheControl: '3600',
                        upsert: true,
                    },
                );
            if (error) {
                notify('error', error && error.message);
                return null;
            }
        } else {
            return null;
        }
    };

    return (
        <LoaderWrapper
            label={
                locale === 'fr' ? 'Téléchargement en cours...' : 'Uploading...'
            }
            loading={isUploading}
        >
            <>
                <label
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    htmlFor="file_input"
                ></label>
                <input
                    accept={allowedFileTypes}
                    className="hidden w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                    id="file_input"
                    onChange={handleFileChange}
                    type="file"
                />
                {!viewDocument && (
                    <div className="flex items-center justify-center w-full ">
                        <label
                            className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                            htmlFor="dropzone-file"
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg
                                    aria-hidden="true"
                                    className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                                    fill="none"
                                    viewBox="0 0 20 16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                    />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">
                                        {dictionary[locale].clickUpload}
                                    </span>{' '}
                                    {dictionary[locale].dragDrop}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {dictionary[locale].allowedFiles}
                                </p>
                            </div>
                            <input
                                className="hidden"
                                id="dropzone-file"
                                onChange={handleFileChange}
                                type="file"
                            />
                        </label>
                    </div>
                )}
                {viewDocument && documentInfo && (
                    <label
                        className="flex flex-col items-center justify-center border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                        htmlFor="document-change"
                    >
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-semibold">
                                {dictionary[locale].clickChange}
                            </span>
                        </p>
                        <figure className="max-w-lg mt-2">
                            <div className="flex justify-center relative">
                                <HiDocumentCheck className="h-16 w-16 fill-gray-700 stroke-gray-900 border-gray-900 cursor-pointer" />
                            </div>
                            {documentInfo && (
                                <figcaption className="mt-2 text-sm text-center text-gray-500 dark:text-gray-400">
                                    {documentInfo.name}
                                </figcaption>
                            )}
                            <input
                                className="hidden"
                                id="document-change"
                                onChange={handleFileChange}
                                type="file"
                            />
                        </figure>
                    </label>
                )}
            </>
        </LoaderWrapper>
    );
};
